import { useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';

const API_BASE = import.meta.env.VITE_API_URL || 'https://ekyc.tumkurconnect.com/api/v1';

interface ApiOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
}

export function useApi() {
  const { accessToken, logout, user } = useAuth();

  const request = useCallback(
    async (endpoint: string, options: ApiOptions = {}) => {
      const url = `${API_BASE}${endpoint}`;
      const isFormData = options.body instanceof FormData;
      const headers: Record<string, string> = {
        ...options.headers,
      };

      // Only auto-set JSON content-type for non-FormData requests
      if (!isFormData && !options.headers?.['Content-Type']) {
        headers['Content-Type'] = 'application/json';
      }

      if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
      }

      // Auto-inject tenant header for consistent backend tenant identification
      const companyId = user?.company?.id;
      if (companyId) {
        headers['x-company-id'] = String(companyId);
      }

      const res = await fetch(url, {
        method: options.method || 'GET',
        headers,
        body: isFormData ? (options.body as FormData) : (options.body ? JSON.stringify(options.body) : undefined),
      });

      if (res.status === 401) {
        logout();
        window.location.href = '/login';
        throw new Error('Session expired. Please log in again.');
      }

      const data = await res.json();

      if (!data.success) {
        throw new Error(data.message || 'Request failed');
      }

      return data;
    },
    [accessToken, logout, user]
  );

  const get = useCallback(
    (endpoint: string) => request(endpoint, { method: 'GET' }),
    [request]
  );

  const post = useCallback(
    (endpoint: string, body: unknown) => request(endpoint, { method: 'POST', body }),
    [request]
  );

  const put = useCallback(
    (endpoint: string, body: unknown) => request(endpoint, { method: 'PUT', body }),
    [request]
  );

  const del = useCallback(
    (endpoint: string) => request(endpoint, { method: 'DELETE' }),
    [request]
  );

  return { request, get, post, put, del };
}
